import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { reverse, sortBy } from 'lodash'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import PanesHeader from 'modules/core/components/PanesHeader'
import i18n from 'modules/core/lib/i18n'

import AllowanceIncidenceModal from 'modules/timeoff/components/AllowanceIncidenceModal'
import allowanceIncidences from 'modules/timeoff/store/collections/allowanceIncidences'
import allowances from 'modules/timeoff/store/collections/allowances'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import Item from './Item'

interface Props {
  employeeId: string
  employeeTimeoffPolicyId: string | null | undefined
  employeeCanManageIncidences: boolean
  timelineItem: PolicyTimelineItem | null | undefined
  hasManualAdjustmentOptions: boolean
}

const AllowanceIncidences = ({
  employeeId,
  employeeTimeoffPolicyId,
  employeeCanManageIncidences,
  timelineItem,
  hasManualAdjustmentOptions,
}: Props) => {
  const [open, toggleModal] = useToggle(false)
  const hasAllowanceIncidences = allowanceIncidences.models.length > 0

  const filteredAllowances = allowances.models.filter(
    (a) => a.attributes.timeoff_policy_id === timelineItem?.timeoff_policy_id
  )

  if (!employeeCanManageIncidences && !hasAllowanceIncidences) return null

  const description = () => {
    if (employeeCanManageIncidences) {
      return i18n.t('time_off.incidences.description_by_hours')
    } else {
      return i18n.t('time_off.incidences.description_employee_hours')
    }
  }

  const renderAllowanceIncidences = () => {
    const filteredAllowanceIds = filteredAllowances.map((a) => a.attributes.id)
    const filteredIncidences = allowanceIncidences.models.filter((aI) =>
      filteredAllowanceIds.includes(aI.attributes.timeoff_allowance_id)
    )
    // Sort the incidences by their effective date.
    const orderedFilteredIncidences = sortBy(
      filteredIncidences,
      (incidence) => incidence.effectiveOn
    )

    // Render an Item for each incidence, passing timelineItem as a required prop.
    return reverse(orderedFilteredIncidences).map((allowanceIncidence) => (
      <Item
        employeeTimeoffPolicyId={employeeTimeoffPolicyId}
        key={allowanceIncidence.id}
        allowanceIncidence={allowanceIncidence}
        canSetAllowanceIncidences={employeeCanManageIncidences}
        timelineItem={timelineItem}
        hasManualAdjustmentOptions={hasManualAdjustmentOptions}
      />
    ))
  }

  return (
    <Box paddingTop="s64" paddingBottom="s32" paddingX="s32">
      {open && (
        <AllowanceIncidenceModal
          employeeId={employeeId}
          onClose={toggleModal(false)}
          timelineItem={timelineItem}
        />
      )}
      <PanesHeader title={i18n.t('time_off.incidences.title')} description={description()} />
      {employeeCanManageIncidences && (
        <Box paddingBottom="s24">
          <Button
            onClick={toggleModal(true)}
            type="secondary"
            extra={{ 'data-intercom-target': 'timeoff-incidences-add-button' }}
          >
            {i18n.t('time_off.incidences.add')}
          </Button>
        </Box>
      )}
      {hasAllowanceIncidences && <BoxList>{renderAllowanceIncidences()}</BoxList>}
    </Box>
  )
}

export default observer(AllowanceIncidences)
